import { Record, RepeatableItemValue } from '@fulcrumapp/fulcrum-core';

const DEFAULT_FETCH_DELAY = 1;
const PAGE_SIZE = 60;

export default class RecordPager {
  form;
  repeatableKey;
  dataSource;
  _perPage;
  _filter;
  _source;
  _totalCount;
  _pages;
  _minPageIndex;
  _currentSourceLastUpdated;
  _stalePages;
  _hasTotalCount;
  _loadingCount;
  _extentCallbacks;
  _error;
  _waitingPages;
  _fetchingPages;
  _maxPageIndex;
  constructor(form, repeatableKey, dataSource, source) {
    this.form = form;
    this.repeatableKey = repeatableKey;
    this.dataSource = dataSource;

    this._perPage = PAGE_SIZE;
    this._filter = null;
    this._source = source;
    this._totalCount = null;

    this.reset();
  }

  get firstPage() {
    return this._pages[0];
  }

  get totalCount() {
    return this._totalCount;
  }

  get filter() {
    return this._filter;
  }

  set filter(filter) {
    this._filter = filter;
    this.reset();
  }

  get minRecordOffset() {
    return Math.max(0, (this._minPageIndex || 0) * this._perPage);
  }

  invalidate() {
    // When modifying the query source, we need to invalidate the record pager from
    // processing results. We cancel the timers above, but it's possible that it's too
    // late and the call has already been made and the callback will fire with the results.
    // At some point we can look into cancelling the AJAX requests, but that has other
    // complexities like requiring a handle to the internal request mechanism to even
    // call abort() on.
    this._currentSourceLastUpdated = this._source.lastUpdated;
  }

  reset() {
    this._stalePages = this._pages;
    this._pages = {};

    // Capture the state of the count in a different variable so we can keep the old count around.
    // This lets the table view retain it's state while it's loading. If we immediately nuke the
    // count, the tableview renders fully blank while it loads, causing it to blink white while
    // reloading.
    this._hasTotalCount = false;
    this._loadingCount = false;
    this._extentCallbacks = [];

    this.clearTimers();

    this._error = null;

    this.invalidate();
  }

  get isStaleResponse() {
    return this._currentSourceLastUpdated != null && (this._currentSourceLastUpdated !== this._source.lastUpdated);
  }

  clearTimers() {
    if (this._waitingPages) {
      for (const key of Object.keys(this._waitingPages)) {
        clearTimeout(this._waitingPages[key]);
      }
    }

    this._fetchingPages = {};
    this._waitingPages = {};
  }

  rowAtIndex(rowIndex, { allowStale }) {
    const pageIndex = this.pageIndexForRow(rowIndex);
    const offset = rowIndex - (pageIndex * this._perPage);

    if (this._pages[pageIndex]) {
      return this._pages[pageIndex][offset];
    }

    // While the next page is loading, allow the currently stored (stale) records to
    // be returned so that it's possible to transition directly from the old data to
    // the new data.
    if (allowStale && this._stalePages && this._stalePages[pageIndex]) {
      return this._stalePages[pageIndex][offset];
    }

    return null;
  }

  fetchExtents(callback) {
    this._loadingCount = true;

    const sql = this._source.toCountSQL();

    const params = { q: sql };

    this.dataSource.source.query(params, (err, rows) => {
      for (const extentCallback of this._extentCallbacks) {
        extentCallback(err, this);
      }

      this._extentCallbacks = [];

      if (this.isStaleResponse) {
        return callback(new Error('stale response'));
      }

      if (err) {
        this._error = err;
        return callback(err);
      }

      this._totalCount = rows[0].total_count;
      this._hasTotalCount = true;

      return callback(null, this);
    });
  }

  fetch(pageIndex, callback) {
    // fetch the total count if we don't have it already
    const fetchPage = (err) => {
      if (err) {
        return callback(err);
      }

      return this.fetchPage(pageIndex, callback);
    };

    if (!this._hasTotalCount) {
      if (!this._loadingCount) {
        this.fetchExtents(fetchPage);
      } else {
        this._extentCallbacks.push(fetchPage);
      }
    } else {
      // @ts-ignore
      fetchPage();
    }
  }

  fetchPage(pageIndex, callback) {
    const sql = this._source.toSQL({ applySort: true, pageSize: this._perPage, pageIndex });

    const params = { q: sql };

    this.dataSource.source.query(params, (err, rows) => {
      if (this.isStaleResponse) {
        return callback(new Error('stale response'));
      }

      if (err) {
        this._error = err;

        return callback(err);
      }

      let records = [];

      if (this.repeatableKey) {
        const repeatable = this.form.get(this.repeatableKey);

        for (const row of rows) {
          row.form_values = JSON.parse(row.form_values);

          const record = new RepeatableItemValue(repeatable, row, row.index);
          // @ts-ignore
          records.push(record);
        }
      } else if (this.form) {
        for (const row of rows) {
          row.form_values = JSON.parse(row.form_values);

          const record = new Record(row, this.form);
          // @ts-ignore
          records.push(record);
        }
      } else {
        records = rows;
      }

      this._pages[pageIndex] = records;

      return callback(null, this);
    });
  }

  setExtents(fromRow, toRow) {
    // Cancels pending requests made to pages that have since-been removed from the viewable
    // area of the grid. When scrolling very quickly we don't want to queue up a ton of work
    // that never ends up being displayed.
    if (fromRow == null) {
      fromRow = 0;
    }
    if (toRow == null) {
      toRow = 0;
    }

    const minPageIndex = this.pageIndexForRow(fromRow);
    const maxPageIndex = this.pageIndexForRow(toRow);

    if (minPageIndex !== this._minPageIndex || maxPageIndex !== this._maxPageIndex) {
      const clearedPages = [];

      for (const pageIndex of Object.keys(this._waitingPages)) {
        // @ts-ignore
        if (pageIndex < minPageIndex || pageIndex > maxPageIndex) {
          clearTimeout(this._waitingPages[pageIndex]);
          // @ts-ignore
          clearedPages.push(pageIndex);
        }
      }

      for (const pageIndex of clearedPages) {
        delete this._waitingPages[pageIndex];
        delete this._fetchingPages[pageIndex];
      }
    }

    this._minPageIndex = minPageIndex;
    this._maxPageIndex = maxPageIndex;
  }

  getRow(row, callback, delay) {
    const pageIndex = this.pageIndexForRow(row);
    const offset = row - (pageIndex * this._perPage);

    if (this._error) {
      return null;
    }

    if (this._pages[pageIndex]) {
      return this._pages[pageIndex][offset];
    }

    if (!this._fetchingPages[pageIndex]) {
      this._fetchingPages[pageIndex] = true;

      this._waitingPages[pageIndex] = setTimeout(() => {
        delete this._waitingPages[pageIndex];

        this.fetch(pageIndex, (fetchError) => {
          delete this._fetchingPages[pageIndex];

          if (fetchError) {
            return callback(fetchError);
          }

          // Invoke the handler which is currently wired up to the top of the react graph
          // and causes everything to be re-rendered
          // this._source._loadHandler();

          if (callback) {
            /* eslint-disable callback-return */
            callback(null, this);
            /* eslint-enable callback-return */
          }

          return null;
        });
      }, delay || DEFAULT_FETCH_DELAY);
    }

    if (this._stalePages && this._stalePages[pageIndex]) {
      return this._stalePages[pageIndex][offset];
    }

    return null;
  }

  pageIndexForRow(row) {
    return Math.floor(row / this._perPage);
  }
}
